import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultPageLayout } from '../layouts/DefaultPageLayout';
import { Button } from '../ui/button';
import { 
  FeatherArrowLeft,
  FeatherPlus,
  FeatherFile,
  FeatherEdit2,
  FeatherXCircle,
  FeatherCheckCircle,
  FeatherUsers,
  FeatherClock,
  FeatherAlertTriangle,
  FeatherActivity,
  FeatherTarget,
  FeatherBarChart,
  FeatherPackage,
  FeatherGrid,
  FeatherList
} from '../ui/icons';
import { Badge } from '../ui/badge';
import { IconWithBackground } from '../ui/IconWithBackground';
import { isManager } from '../../utils/roleUtils';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatLastActivity, calculateCompletionPercentage, formatHours, getProgressColor } from '../../utils/projectUtils';
import VersionCheckBanner from '../shared/VersionCheckBanner';
import { releasesApi } from '../../services/api';
import './Projects.css';
import ProjectForm from './ProjectForm';

const Projects = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
    const { user } = useAuth();
    const [userIsManager, setUserIsManager] = useState(false);
    const [releasesData, setReleasesData] = useState({});

    useEffect(() => {
        if (user?.id) {
            checkUserRole();
            fetchProjects();
        }
    }, [user?.id, showInactive]);

    const checkUserRole = async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
        } catch (err) {
            console.error('Error checking user role:', err);
            setError('Failed to check user permissions');
        }
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const params = showInactive ? { includeInactive: 'true' } : {};
            const response = await api.get('/api/projects', { params });
            const projectsData = response.data || [];
            
            // Fetch releases for all projects in parallel
            const releasesPromises = projectsData.map(async (project) => {
                try {
                    const releases = await releasesApi.getProjectReleases(project.id);
                    return { projectId: project.id, releases };
                } catch (err) {
                    console.error(`Error fetching releases for project ${project.id}:`, err);
                    return { projectId: project.id, releases: [] };
                }
            });
            
            const releasesResults = await Promise.all(releasesPromises);
            const releasesMap = releasesResults.reduce((map, result) => {
                map[result.projectId] = result.releases;
                return map;
            }, {});
            setReleasesData(releasesMap);
            
            // Sort projects by last activity (most recent first)
            // Projects with no activity go to the end, sorted by created_at
            const sortedProjects = [...projectsData].sort((a, b) => {
                const aLastActivity = a.insights?.lastActivityDate ? new Date(a.insights.lastActivityDate).getTime() : 0;
                const bLastActivity = b.insights?.lastActivityDate ? new Date(b.insights.lastActivityDate).getTime() : 0;
                
                // Projects with activity come first
                if (aLastActivity === 0 && bLastActivity === 0) {
                    // Both have no activity - sort by created_at (newest first)
                    const aCreated = new Date(a.created_at).getTime();
                    const bCreated = new Date(b.created_at).getTime();
                    return bCreated - aCreated;
                }
                
                if (aLastActivity === 0) return 1; // a has no activity, goes to end
                if (bLastActivity === 0) return -1; // b has no activity, goes to end
                
                // Both have activity - sort by most recent first
                return bLastActivity - aLastActivity;
            });
            
            setProjects(sortedProjects);
            
            setError('');
        } catch (err) {
            setError('Failed to fetch projects');
            console.error('Error fetching projects:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleProjectCreated = async (formData) => {
        try {
            await api.post('/api/projects', formData);
            setShowForm(false);
            // Refetch projects to get insights for the new project
            await fetchProjects();
        } catch (err) {
            console.error('Error creating project:', err);
            throw new Error(err.response?.data?.error || 'Failed to create project');
        }
    };

    const handleProjectClick = (projectId) => {
        navigate(`/projects/${projectId}`);
    };

    const handleDeleteProject = async (projectId) => {
        try {
            await api.delete(`/api/projects/${projectId}`);
            setProjects(prev => prev.filter(project => project.id !== projectId));
        } catch (err) {
            console.error('Error deleting project:', err);
            setError('Failed to delete project');
        }
    };

    const handleToggleActiveStatus = async (projectId, currentStatus, e) => {
        e.stopPropagation();
        try {
            const newStatus = !currentStatus;
            await api.patch(`/api/projects/${projectId}/active-status`, { is_active: newStatus });
            
            // Optimistically update the project
            setProjects(prev => prev.map(project => 
                project.id === projectId 
                    ? { ...project, is_active: newStatus }
                    : project
            ));
        } catch (err) {
            console.error('Error updating project active status:', err);
            setError('Failed to update project status');
        }
    };

    if (loading) {
        return (
            <>
                <VersionCheckBanner />
                <div className="loading-container">
                    <div className="loading">Loading projects...</div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <VersionCheckBanner />
                <div className="error-container">
                    <div className="error">{error}</div>
                </div>
            </>
        );
    }

    return (
        <DefaultPageLayout>
            <VersionCheckBanner />
            <div className="projects-container">
                <div className="header">
                    <div className="header-left">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherArrowLeft />}
                            onClick={() => navigate("/thehub")}
                        >
                            Back
                        </Button>
                        <div className="flex items-center gap-2">
                            <h1 className="text-heading-2">Projects</h1>
                        </div>
                    </div>
                    <div className="header-right">
                        <div className="flex items-center gap-2">
                            <Button
                                variant="neutral-tertiary"
                                icon={viewMode === 'grid' ? <FeatherList /> : <FeatherGrid />}
                                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                            >
                                {viewMode === 'grid' ? 'List' : 'Grid'}
                            </Button>
                        <Button
                            variant="neutral-tertiary"
                            onClick={() => setShowInactive(!showInactive)}
                        >
                            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                        </Button>
                        {userIsManager && (
                            <Button
                                variant="brand-primary"
                                icon={<FeatherPlus />}
                                onClick={() => setShowForm(true)}
                            >
                                New Project
                            </Button>
                        )}
                        </div>
                    </div>
                </div>

                {showForm && (
                    <div className="project-form-container">
                        <h2 className="text-heading-3">Create New Project</h2>
                        <ProjectForm
                            onSuccess={handleProjectCreated}
                            onCancel={() => setShowForm(false)}
                        />
                    </div>
                )}

                {viewMode === 'grid' ? (
                <div className="projects-grid">
                    {projects.map(project => {
                        const insights = project.insights || {};
                        const taskCounts = insights.taskCounts || { total: 0, active: 0, completed: 0 };
                        const hasNoBacklog = insights.backlogCount === 0 && taskCounts.total > 0;
                        const completionPercentage = insights.completionPercentage || 0;
                        
                        // Calculate release stats
                        const releases = releasesData[project.id] || [];
                        const totalReleases = releases.length;
                        const activeReleases = releases.filter(r => r.status === 'in_progress').length;
                        const completedReleases = releases.filter(r => r.status === 'completed').length;
                        const plannedReleases = releases.filter(r => r.status === 'planned').length;
                        
                        // Calculate overall release progress
                        let totalReleaseProgress = 0;
                        let releasesWithProgress = 0;
                        releases.forEach(release => {
                            if (release.insights && release.insights.completion_percentage !== undefined) {
                                totalReleaseProgress += release.insights.completion_percentage;
                                releasesWithProgress++;
                            }
                        });
                        const avgReleaseProgress = releasesWithProgress > 0 
                            ? Math.round(totalReleaseProgress / releasesWithProgress) 
                            : 0;
                        
                        return (
                            <div
                                key={project.id}
                                className={`project-card ${!project.is_active ? 'project-card-inactive' : ''}`}
                                onClick={() => handleProjectClick(project.id)}
                            >
                                <div className="project-card-header">
                                    <div className="flex items-center gap-2">
                                        <IconWithBackground icon={<FeatherFile />} />
                                        <div className="flex items-center gap-2 grow">
                                            <span className="text-body-bold font-body-bold text-default-font">
                                                {project.name}
                                                {project.abbreviation && (
                                                    <span className="project-abbreviation"> [{project.abbreviation}]</span>
                                                )}
                                            </span>
                                            {hasNoBacklog && (
                                                <FeatherCheckCircle 
                                                    size={20} 
                                                    style={{ color: '#10b981', marginLeft: '4px' }}
                                                    title="No backlog - all tasks assigned"
                                                />
                                            )}
                                            <Badge variant={project.is_active ? 'success' : 'neutral'}>
                                                {project.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                        {userIsManager && (
                                            <Button
                                                variant="neutral-tertiary"
                                                className="text-xs px-2 py-1"
                                                onClick={(e) => handleToggleActiveStatus(project.id, project.is_active, e)}
                                                title={project.is_active ? 'Mark as inactive' : 'Mark as active'}
                                            >
                                                {project.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                
                                {project.description && (
                                    <div className="project-description">
                                        {project.description}
                                    </div>
                                )}

                                <div className="project-insights">
                                    <div className="insights-grid">
                                        {/* Task Counts */}
                                        <div className="insight-item">
                                            <div className="insight-label">
                                                <FeatherBarChart size={16} />
                                                Tasks
                                            </div>
                                            <div className="insight-value">
                                                {taskCounts.total === 0 ? (
                                                    <span className="insight-main" style={{ fontStyle: 'italic', color: '#9ca3af' }}>
                                                        No tasks present
                                                    </span>
                                                ) : (
                                                    <>
                                                <span className="insight-main">{taskCounts.total}</span>
                                                <span className="insight-sub">
                                                    {taskCounts.active} active, {taskCounts.completed} done
                                                </span>
                                                    </>
                                                )}
                                            </div>
                                            {taskCounts.total > 0 && (
                                                <div className="progress-bar">
                                                    <div 
                                                        className="progress-fill" 
                                                        style={{ width: `${completionPercentage}%` }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Assigned Tasks (Open or In progress) */}
                                        <div className="insight-item insight-item-assigned">
                                            <div className="insight-label">
                                                <FeatherUsers size={16} />
                                                Assigned
                                            </div>
                                            <div className="insight-value">
                                                <span className="insight-main insight-assigned">{insights.assignedTasksCount || 0}</span>
                                                <span className="insight-sub">tasks</span>
                                            </div>
                                        </div>

                                        {/* Unassigned Tasks */}
                                        <div className="insight-item insight-item-unassigned">
                                            <div className="insight-label">
                                                <FeatherAlertTriangle size={16} />
                                                Unassigned
                                            </div>
                                            <div className="insight-value">
                                                {insights.unassignedTasksCount > 0 ? (
                                                    <>
                                                        <span className="insight-main insight-unassigned">{insights.unassignedTasksCount}</span>
                                                        <span className="insight-sub">tasks</span>
                                                    </>
                                                ) : (
                                                    taskCounts.total > 0 && insights.assignedTasksCount > 0 ? (
                                                        <span className="insight-main" style={{ fontStyle: 'italic', color: '#9ca3af', fontSize: '0.875rem' }}>
                                                            No backlogs present
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span className="insight-main insight-unassigned">0</span>
                                                <span className="insight-sub">tasks</span>
                                                        </>
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {/* Team Size */}
                                        <div className="insight-item">
                                            <div className="insight-label">
                                                <FeatherUsers size={16} />
                                                Team
                                            </div>
                                            <div className="insight-value">
                                                <span className="insight-main">{insights.teamSize || 0}</span>
                                                <span className="insight-sub">members</span>
                                            </div>
                                        </div>

                                        {/* Last Activity */}
                                        <div className="insight-item">
                                            <div className="insight-label">
                                                <FeatherClock size={16} />
                                                Last Activity
                                            </div>
                                            <div className="insight-value">
                                                <span className="insight-main">
                                                    {formatLastActivity(insights.lastActivityDate)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Overdue Features */}
                                        {insights.overdueFeaturesCount > 0 && (
                                            <div className="insight-item insight-warning">
                                                <div className="insight-label">
                                                    <FeatherAlertTriangle size={16} />
                                                    Overdue
                                                </div>
                                                <div className="insight-value">
                                                    <span className="insight-main">{insights.overdueFeaturesCount}</span>
                                                    <span className="insight-sub">features</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Recent Activity */}
                                        <div className="insight-item">
                                            <div className="insight-label">
                                                <FeatherActivity size={16} />
                                                Recent
                                            </div>
                                            <div className="insight-value">
                                                <span className="insight-main">{insights.recentWorkPlanActivity || 0}</span>
                                                <span className="insight-sub">work plans (7d)</span>
                                            </div>
                                        </div>

                                        {/* Completion */}
                                        <div className="insight-item">
                                            <div className="insight-label">
                                                <FeatherTarget size={16} />
                                                Complete
                                            </div>
                                            <div className="insight-value">
                                                <span className="insight-main">{completionPercentage}%</span>
                                            </div>
                                        </div>

                                        {/* Hours Logged */}
                                        {insights.hoursLogged > 0 && (
                                            <div className="insight-item">
                                                <div className="insight-label">
                                                    <FeatherClock size={16} />
                                                    Hours
                                                </div>
                                                <div className="insight-value">
                                                    <span className="insight-main">{formatHours(insights.hoursLogged)}</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Releases */}
                                        {totalReleases > 0 && (
                                            <div className="insight-item">
                                                <div className="insight-label">
                                                    <FeatherPackage size={16} />
                                                    Releases
                                                </div>
                                                <div className="insight-value">
                                                    <span className="insight-main">{totalReleases}</span>
                                                    <span className="insight-sub">
                                                        {activeReleases} active, {completedReleases} done
                                                    </span>
                                                </div>
                                                {avgReleaseProgress > 0 && (
                                                    <div className="progress-bar">
                                                        <div 
                                                            className="progress-fill" 
                                                            style={{ 
                                                                width: `${avgReleaseProgress}%`,
                                                                backgroundColor: getProgressColor(avgReleaseProgress)
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="project-footer">
                                    <div className="flex items-center gap-2">
                                        <span className="text-caption font-caption text-subtext-color">
                                            Created by: {project.created_by?.full_name || 'Unknown'}
                                        </span>
                                    </div>
                                    {userIsManager && (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="neutral-tertiary"
                                                icon={<FeatherEdit2 />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/projects/${project.id}/edit`);
                                                }}
                                                disabled={true}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="neutral-tertiary"
                                                icon={<FeatherXCircle />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteProject(project.id);
                                                }}
                                                disabled={true}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    </div>
                ) : (
                    <div className="projects-list">
                        <div className="projects-list-header">
                            <div className="list-col-project">Project</div>
                            <div className="list-col-status">Status</div>
                            <div className="list-col-tasks">Tasks</div>
                            <div className="list-col-completion">Completion</div>
                            <div className="list-col-team">Team</div>
                            <div className="list-col-activity">Last Activity</div>
                            <div className="list-col-recent">Recent (7d)</div>
                            <div className="list-col-actions">Actions</div>
                        </div>
                        {projects.map(project => {
                            const insights = project.insights || {};
                            const taskCounts = insights.taskCounts || { total: 0, active: 0, completed: 0 };
                            const hasNoBacklog = insights.backlogCount === 0 && taskCounts.total > 0;
                            const completionPercentage = insights.completionPercentage || 0;
                            
                            // Calculate release stats
                            const releases = releasesData[project.id] || [];
                            const totalReleases = releases.length;
                            const activeReleases = releases.filter(r => r.status === 'in_progress').length;
                            const completedReleases = releases.filter(r => r.status === 'completed').length;
                            
                            // Calculate overall release progress
                            let totalReleaseProgress = 0;
                            let releasesWithProgress = 0;
                            releases.forEach(release => {
                                if (release.insights && release.insights.completion_percentage !== undefined) {
                                    totalReleaseProgress += release.insights.completion_percentage;
                                    releasesWithProgress++;
                                }
                            });
                            const avgReleaseProgress = releasesWithProgress > 0 
                                ? Math.round(totalReleaseProgress / releasesWithProgress) 
                                : 0;
                            
                            // Determine if there are alerts
                            const hasAlerts = insights.overdueFeaturesCount > 0 || 
                                             insights.unassignedTasksCount > 0 || 
                                             insights.backlogCount > 0;
                            
                            return (
                                <div
                                    key={project.id}
                                    className={`project-list-item ${!project.is_active ? 'project-list-item-inactive' : ''} ${hasAlerts ? 'project-list-item-has-alerts' : ''}`}
                                    onClick={() => handleProjectClick(project.id)}
                                >
                                    {/* Primary Row */}
                                    <div className="project-list-row-primary">
                                        <div className="list-col-project">
                                            <div className="flex items-center gap-2">
                                                <IconWithBackground icon={<FeatherFile />} />
                                            <div className="flex flex-col" style={{ gap: '0.25rem' }}>
                                                <div className="flex items-center gap-2">
                                                        <span className="text-body-bold font-body-bold text-default-font">
                                                            {project.name}
                                                            {project.abbreviation && (
                                                                <span className="project-abbreviation"> [{project.abbreviation}]</span>
                                                            )}
                                                        </span>
                                                        {hasNoBacklog && (
                                                            <FeatherCheckCircle 
                                                                size={16} 
                                                                style={{ color: '#10b981' }}
                                                                title="No backlog - all tasks assigned"
                                                            />
                                                        )}
                                                    </div>
                                                    {project.description && (
                                                        <span className="text-caption font-caption text-subtext-color project-list-description">
                                                            {project.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="list-col-status">
                                            <Badge variant={project.is_active ? 'success' : 'neutral'}>
                                                {project.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                        <div className="list-col-tasks">
                                            {taskCounts.total === 0 ? (
                                                <span className="text-caption font-caption text-subtext-color" style={{ fontStyle: 'italic' }}>
                                                    No tasks present
                                                </span>
                                            ) : (
                                                <div className="flex flex-col">
                                                    <span className="text-body font-body text-default-font">
                                                        <strong>{taskCounts.total}</strong>
                                                    </span>
                                                    <span className="text-caption font-caption text-subtext-color">
                                                        {taskCounts.active} active, {taskCounts.completed} done
                                                    </span>
                                                    {taskCounts.total > 0 && (
                                                        <div className="progress-bar progress-bar-compact">
                                                            <div 
                                                                className="progress-fill" 
                                                                style={{ width: `${completionPercentage}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="list-col-completion">
                                            <div className="flex items-center gap-2">
                                                <span className="text-body font-body text-default-font" style={{ 
                                                    color: getProgressColor(completionPercentage),
                                                    fontWeight: 600
                                                }}>
                                                    {completionPercentage}%
                                                </span>
                                            </div>
                                        </div>
                                        <div className="list-col-team">
                                            <div className="flex items-center gap-2">
                                                <FeatherUsers className="list-icon-primary" style={{ color: '#6b7280' }} />
                                                <span className="text-body font-body text-default-font">
                                                    {insights.teamSize || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="list-col-activity">
                                            <div className="flex items-center gap-3">
                                                <FeatherClock className="list-icon-primary" size={12} style={{ color: '#6b7280' }} />
                                                <span className="text-body font-body text-default-font">
                                                    {formatLastActivity(insights.lastActivityDate)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="list-col-recent">
                                            <div className="flex items-center gap-2">
                                                <FeatherActivity className="list-icon-primary" style={{ color: '#6b7280' }} />
                                                <span className="text-body font-body text-default-font">
                                                    {insights.recentWorkPlanActivity || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="list-col-actions">
                                            {userIsManager && (
                                                <Button
                                                    variant="neutral-tertiary"
                                                    className="text-xs px-2 py-1"
                                                    onClick={(e) => handleToggleActiveStatus(project.id, project.is_active, e)}
                                                    title={project.is_active ? 'Mark as inactive' : 'Mark as active'}
                                                >
                                                    {project.is_active ? 'Deactivate' : 'Activate'}
                                                </Button>
                                            )}
                                        </div>
                </div>
                                    
                                    {/* Secondary Row - Alerts and Additional Info */}
                                    {(hasAlerts || insights.hoursLogged > 0 || totalReleases > 0 || project.created_by?.full_name) && (
                                        <div className="project-list-row-secondary">
                                            <div className="list-col-project">
                                                <span className="text-caption font-caption text-subtext-color">
                                                    Created by: {project.created_by?.full_name || 'Unknown'}
                                                </span>
                                            </div>
                                            <div className="list-col-status"></div>
                                            <div className="list-col-tasks">
                                                {taskCounts.total === 0 ? (
                                                    <span className="text-caption font-caption text-subtext-color" style={{ fontStyle: 'italic' }}>
                                                        No tasks present
                                                    </span>
                                                ) : (
                                                    insights.assignedTasksCount > 0 && (
                                                        <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                            <FeatherUsers className="list-icon-secondary" style={{ color: '#ff8c69', opacity: 0.8 }} />
                                                            <span className="text-caption font-caption" style={{ color: '#ff8c69' }}>
                                                                {insights.assignedTasksCount} assigned
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            <div className="list-col-completion"></div>
                                            <div className="list-col-team">
                                                {taskCounts.total === 0 ? null : (
                                                    insights.unassignedTasksCount > 0 ? (
                                                        <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                            <FeatherAlertTriangle className="list-icon-secondary" style={{ color: '#5fb3b3', opacity: 0.8 }} />
                                                            <span className="text-caption font-caption" style={{ color: '#5fb3b3' }}>
                                                                {insights.unassignedTasksCount} unassigned
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        insights.assignedTasksCount > 0 && (
                                                            <span className="text-caption font-caption text-subtext-color" style={{ fontStyle: 'italic' }}>
                                                                No backlogs present
                                                            </span>
                                                        )
                                                    )
                                                )}
                                            </div>
                                            <div className="list-col-activity">
                                                {insights.overdueFeaturesCount > 0 && (
                                                    <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                        <FeatherAlertTriangle className="list-icon-secondary" style={{ color: '#ef4444', opacity: 0.8 }} />
                                                        <span className="text-caption font-caption" style={{ color: '#ef4444' }}>
                                                            {insights.overdueFeaturesCount} overdue
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="list-col-recent">
                                                <div className="flex flex-col" style={{ gap: '0.25rem' }}>
                                                    {insights.hoursLogged > 0 && (
                                                        <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                            <FeatherClock className="list-icon-secondary" size={10} style={{ color: '#9ca3af', opacity: 0.7 }} />
                                                            <span className="text-caption font-caption text-subtext-color">
                                                                {formatHours(insights.hoursLogged)} total
                                                            </span>
                                                        </div>
                                                    )}
                                                    {insights.hoursThisMonth > 0 && (
                                                        <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                            <FeatherClock className="list-icon-secondary" size={10} style={{ color: '#6366f1', opacity: 0.8 }} />
                                                            <span className="text-caption font-caption" style={{ color: '#888bf3', fontWeight: 500 }}>
                                                                {formatHours(insights.hoursThisMonth)} this month
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="list-col-actions">
                                                {totalReleases > 0 && (
                                                    <div className="flex items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                                                        <div className="flex items-center" style={{ gap: '0.375rem' }}>
                                                            <FeatherPackage className="list-icon-secondary" size={10} style={{ color: '#9ca3af', opacity: 0.7 }} />
                                                            <span className="text-caption font-caption text-subtext-color">
                                                                {totalReleases} release{totalReleases !== 1 ? 's' : ''}
                                                                {activeReleases > 0 && ` (${activeReleases} active)`}
                                                            </span>
                                                        </div>
                                                        {avgReleaseProgress > 0 && (
                                                            <div className="progress-bar progress-bar-compact" style={{ width: '60px', marginLeft: '0' }}>
                                                                <div 
                                                                    className="progress-fill" 
                                                                    style={{ 
                                                                        width: `${avgReleaseProgress}%`,
                                                                        backgroundColor: getProgressColor(avgReleaseProgress)
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {projects.length === 0 && (
                    <div className="no-projects">
                        No projects found
                    </div>
                )}
            </div>
        </DefaultPageLayout>
    );
};

export default Projects; 